import {
  DebtRepayment,
  DebtRepaymentPlan,
  DebtRepaymentPlanStatus,
  DebtRepaymentService,
  DebtRepaymentSource,
  displayCentsInDollars,
} from '@vgrubs/components';

/**
 * The backend takes a per order or per delivery amount of $0.01-$4.00 (in
 * cents, 1-400; $1.00 was the minimum before Oct 8, 2026). Off is `null`,
 * never 0: 0 fails its validation.
 */
export const PLAN_AMOUNT_MIN = 1;
export const PLAN_AMOUNT_MAX = 400;
export const PLAN_NOTE_MAX = 1000;

/** The blue card collection runs at these times, New York. */
export const BALANCE_TIMES = '11:00 & 21:00';

/**
 * The service a plan or a collection repays. A backend from before Oct 22,
 * 2026 sends none: it only had vDrive plans.
 */
export const planService = (row: { service?: DebtRepaymentService }) =>
  row.service ?? 'vDrive';

/** What each plan pays back, by the names the page uses. */
export const SERVICE_DEBT_NAMES: Record<DebtRepaymentService, string> = {
  vDrive: 'Previous Transaction Due Balance',
  saas: 'past vGrubs Monthly Fees',
};

/** The same at the start of a sentence: "Past vGrubs Monthly Fees". */
export const serviceDebtTitle = (service: DebtRepaymentService) =>
  SERVICE_DEBT_NAMES[service].charAt(0).toUpperCase() +
  SERVICE_DEBT_NAMES[service].slice(1);

export const SERVICE_PLAN_TITLES: Record<DebtRepaymentService, string> = {
  vDrive: 'vDrive repayment plan',
  saas: 'SaaS repayment plan',
};

/** "$5.00 a day", or null without a limit. */
export const dailyLimitLabel = (plan: {
  fromLocationBalance: boolean;
  dailyBalanceLimit?: number | null;
}) =>
  plan.fromLocationBalance && plan.dailyBalanceLimit != null
    ? `${displayCentsInDollars(plan.dailyBalanceLimit)} a day`
    : null;

export const PLAN_STATUS_LABELS: Record<DebtRepaymentPlanStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Stopped',
};

export const REPAYMENT_SOURCE_NAMES: Record<DebtRepaymentSource, string> = {
  locationBalance: 'vGrubs balance (vPay Card)',
  virtualBrandOrder: 'Order',
  vDriveDelivery: 'vDrive delivery',
};

/**
 * Where a collection came from, as the spec words it: "Order #123",
 * "vDrive delivery #45", or the blue card.
 */
export const repaymentSourceLabel = (repayment: DebtRepayment) => {
  const name = REPAYMENT_SOURCE_NAMES[repayment.source] ?? repayment.source;
  const id =
    repayment.source === 'virtualBrandOrder'
      ? repayment.orderId
      : repayment.source === 'vDriveDelivery'
      ? repayment.orderVDriveId
      : null;
  return id != null ? `${name} #${id}` : name;
};

/**
 * The methods a plan has on, one entry each, e.g. ["vGrubs balance 11:00 &
 * 21:00", "$2.00 per order", "$1.50 per delivery"]; with a daily limit the
 * first one is "vGrubs balance up to $5.00 a day".
 */
export const planMethods = (plan: DebtRepaymentPlan) =>
  [
    plan.fromLocationBalance &&
      (dailyLimitLabel(plan)
        ? `vGrubs balance up to ${dailyLimitLabel(plan)}`
        : `vGrubs balance ${BALANCE_TIMES}`),
    plan.perOrderAmount != null &&
      `${displayCentsInDollars(plan.perOrderAmount)} per order`,
    plan.perDeliveryAmount != null &&
      `${displayCentsInDollars(plan.perDeliveryAmount)} per delivery`,
  ].filter((method): method is string => !!method);

/**
 * The same methods as label / value rows, for a summary laid out as a list:
 * [{ label: 'vGrubs balance', value: '11:00 & 21:00' }, ...].
 */
export const planMethodRows = (plan: DebtRepaymentPlan) =>
  [
    plan.fromLocationBalance && {
      label: 'vGrubs balance',
      value: dailyLimitLabel(plan)
        ? `Up to ${dailyLimitLabel(plan)}`
        : BALANCE_TIMES,
    },
    plan.perOrderAmount != null && {
      label: 'Per order',
      value: displayCentsInDollars(plan.perOrderAmount),
    },
    plan.perDeliveryAmount != null && {
      label: 'Per delivery',
      value: displayCentsInDollars(plan.perDeliveryAmount),
    },
  ].filter((row): row is { label: string; value: string } => !!row);

export const planMethodsLine = (plan: DebtRepaymentPlan) =>
  planMethods(plan).join(' · ');

/** When a closed plan ended, or when an active one started. */
export const planEndedAt = (plan: DebtRepaymentPlan) =>
  plan.status === 'completed'
    ? plan.completedAt
    : plan.status === 'cancelled'
    ? plan.cancelledAt
    : null;
